/* eslint-disable @nx/enforce-module-boundaries */
import { DegreeConnectionsIcon } from '@mybridge/icons/DegreeConnectionsIcon';
import { FollowersIcon } from '@mybridge/icons/Followers';
import { MutualConnectionIcon } from '@mybridge/icons/MutualConnectionIcon';
import {
  Avatar,
  HStack,
  Link,
  Stack,
  Text,
  AvatarBadge,
  Divider,
  Card,
  VStack,
  Box,
  AvatarGroup,
} from '@mybridge/ui';
import { forwardRef, useMemo, useContext } from 'react';
import {
  formatCommaSeparated,
  formatLocation,
  getUserFullName,
  getUserProfilePic,
} from 'v4/lib/commons';
import { MyNetworkContext } from '../context';
import { useRouter } from 'next/router';
import { isMobile } from 'react-device-detect';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useSelector } from 'react-redux';
import TruncatedTextWithTooltip from 'v4/components/common/Tooltip';
import { useNumberFormatter } from '@mybridge/ui/custom-hooks/useNumberFormatter';
import { formatISODate } from 'v4/utils/utils';
export const MyNetworkUserCardBordered = forwardRef(
  (
    {
      user = {},
      actions,
      children,
      showFollowers,
      avatarProps,
      divider = false, // Add isMobile prop
      fromOnboarding=false,
      ...props
    },
    ref
  ) => {
    const router = useRouter();
    const { t } = useTranslator();
    const name = getUserFullName(user);
    const profile_pic = getUserProfilePic(user);
    const currentLanguage = useSelector((state) => state.translation.language) || 'en';
    const {
      position,
      company,
      experiences,
      educations,
      slug,
      degree,
      degree_count,
      mutual_count,
      followers_count,
      matrix_presence,
      profile_banner_pic,
      mutual_connections,
      mutual_connection_count,
    } = user ?? {};
    const networkCtx = useContext(MyNetworkContext);
    const {
      isFollowed,
      isFriend,
      followUser,
      handshakeUser,
      handshakeLoading,
      followLoading,
      isHandshaked,
      setSelectedUser,
    } = networkCtx ?? {};

    const degree_ = degree_count ?? degree;
    const { formatNumber } = useNumberFormatter();
    const location = useMemo(() => {
      let loc = formatLocation(user);
      return loc?.length ? loc : user?.location;
    }, [user]);

    const MATRIX_PRESENCE = {
      online: {
        bgColor: 'green.500',
        priority: 2,
      },
      busy: {
        bgColor: 'red.500',
        priority: 1,
      },
      offline: {
        bgColor: 'gray.500',
        priority: 0,
      },
    };

    return (
      <>
        <Card
          variant="thinBorderedCard"
          minW={['160', '148px']}
          maxW={['none', '148px']}
          w="100%"
          overflow="hidden"
          borderRadius="20px"
          border="1px solid #D9D9D9"
          boxShadow="none"
          // maxH="240px"
          minH="235px"
          gap={0}
        >
          <VStack
            direction={['column', 'row']}
            alignItems="flex-start"
            {...props}
          >
            <HStack w="100%" justifyContent="center" position="relative">
              <Box
                // bgImage={
                //   profile_banner_pic &&
                //   !profile_banner_pic.includes('banner-placeholder1.png')
                //     ? profile_banner_pic
                //     : '/images/defaultBanner.svg'
                // }
                bgPosition="center"
                bgSize="cover"
                position="absolute"
                left="0"
                top="0"
                right="0"
                height="60px"
              ></Box>
              <Avatar
                {...(slug?.length ? { as: Link, href: '/p/' + slug } : {})}
                name={name}
                width="80px"
                height="80px"
                fontSize="22px"
                {...(profile_pic?.length ? { src: profile_pic } : {})}
                {...(avatarProps ?? {})}
                mt={2}
                border="2px solid white"
              />
            </HStack>

            <HStack
              flex={1}
              minH="30px"
              alignItems="center"
              justifyContent="center"
              width="100%"
            >
              <Stack spacing={0} flex={1} align="center">
                {name ? (
                  <HStack justifyContent="center">
                    {/* Center the name */}
                    <Text
                      fontSize="14px"
                      fontWeight="500"
                      color="brandPrimary.500"
                      textTransform="capitalize"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                      display="inline-block"
                      maxW="130px"
                      {...(slug?.length
                        ? { as: Link, href: '/p/' + slug }
                        : {})}
                    >
                      {name}
                    </Text>
                  </HStack>
                ) : null}

                <Box>
                  <Stack minH="30px" spacing={0} alignItems="center">
                    {/* Center align items */}
                    {experiences?.length ? (
                      <>
                        <Text
                          fontSize="10px"
                          m={0}
                          fontWeight="600"
                          color="#868585"
                          maxW="160px"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                        >
                          {experiences?.[0]?.title?.length>25?experiences?.[0]?.title?.slice(0, 25)+ '...' :experiences?.[0]?.title}
                        </Text>
                        <Text
                          fontSize="10px"
                          m={0}
                          fontWeight="400"
                          color="#868585"
                          maxW="160px"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                        >
                          {experiences?.[0]?.company?.length > 25 ?
                            experiences?.[0]?.company_name?.slice(0, 25)+ '...' :
                            experiences?.[0]?.company_name}
                        </Text>
                      </>
                    ) : educations?.length ? (
                      <Text
                        fontSize="10px"
                        maxW="160px"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                      >
                        {educations?.[0]?.name?.length>25?educations?.[0]?.name?.slice(0,10)+"...":educations?.[0]?.name}
                      </Text>
                    ) : (
                      <>
                        <Text fontSize="10px" textAlign="center">
                          {/* Center the position text */}
                          {position?.length > 25
                            ? position?.slice(0, 25) + '...'
                            : position}
                        </Text>
                        <Text
                          fontSize="10px"
                          color="#868585"
                          textAlign="center"
                        >
                          {/* Center the company text */}
                          {company}
                        </Text>
                      </>
                    )}
                  </Stack>

                  {props?.from==='sent-invite'&&(
                    <HStack justifyContent='center'>
                      <Text fontSize="9px" color="#868585" lineHeight="1.2">
                        {formatISODate(props?.createdAt)}
                      </Text>
                    </HStack>
                  )}

                  {mutual_connections?.length ? (
                    <HStack
                      spacing={0}
                      alignItems="center" // Center align items
                      onClick={() =>
                        router.push(`/profile/network/${user.slug}`)
                      }
                      cursor="pointer"
                      minH={2}
                      p={"3px"}
                      justifyContent="center" // Center the mutual connections section
                    >
                      <AvatarGroup color="blackAlpha.500" size="sm" max={3}>
                        {mutual_connections?.map?.((u, uInd) => (
                          <Avatar
                            as={Link}
                            href={'/p/' + u?.slug}
                            key={u?.id}
                            width="18px"
                            height="18px"
                            src={getUserProfilePic(u)}
                            name={getUserFullName(u)}
                            border="1px solid #fff"
                            fontSize="8px"
                            marginRight="-5px !important"
                            webkitMarginEnd="none"
                            marginInlineEnd="none"
                            display="flex"
                          />
                        ))}
                      </AvatarGroup>
                        <Text fontSize="9px" color="#868585" ms="8px" mt="1px">
                            {mutual_connection_count > 3 ? `+${formatNumber(mutual_connection_count - 3)}` : ''}{' '}
                            {currentLanguage === 'en' ? (
                              t('profileNetwork.sharedConnections') || "shared connections"
                            ) : (
                              <TruncatedTextWithTooltip
                                text={t('profileNetwork.sharedConnections') || "shared connections"}
                                maxChars={15}
                              />
                            )}
                          </Text>
                    </HStack>
                  ) : (
                    <HStack spacing={0} alignItems="flex-start" minH={'20px'} />
                  )}
                </Box>

                {children}
              </Stack>
            </HStack>

            <Stack h="100%" w="100%" justifyContent="center" px={3} pb={3}>
              {actions}
            </Stack>
          </VStack>
        </Card>
      </>
    );
  }
);
